import { notification } from 'antd'
import store from 'store'
import i18n from 'i18n'
import { getUserDisplayName, getUserAvatar, getUserID, DEFAULT_AUTH_PAGE } from 'utils'
import history from 'history'
import helper from 'pages/records/components/OrgChartTree/helper'

import { store as reduxStore } from 'index'
import { syncRecoveryPubKeyFromAuthData } from 'pUtils'
import {
  getEnvParam,
  setEnvParam,
  removeEnvParam,
  setTempEnvParam,
  getTempEnvParam,
  removeTempEnvParam,
} from 'env'

import { errorBase } from 'utils/ui'
import { getSettings } from 'services/landing'
import { captureForcedLogout } from 'services/sentryLog'
import { handleAccessBlocked } from 'services/jwt'
import { removeLocalAppUserKeys } from 'utils/localUserKeys'
import {
  getMyInfo,
  getMyAvatar,
  getWorkspacesByEmail,
  getAuthInfo,
  getCommonAuthWorkspaceInfo,
  selectWorkspace,
  getWorkspaceInfo,
  getPaidLicense,
} from 'services/landing/utils/api'

// 로그인 체인의 접근 차단(870 IP/879 해외) — customFetch 전역 분기는 /auth 경로에서 침묵하므로
// (services/jwt customFetch의 pathname 가드) 로그인 플로우가 자체 처리한다. LOGOUT saga의
// logout()은 SAML IdP 하드 리다이렉트가 /ip-blocked 이동을 덮어쓰므로 로컬 토큰 정리로 대체
// (pUtils.login과 동일 정책 — QA round1 #1566). 토큰이 지워지면 saga 'ip-blocked' 케이스의
// token 가드가 logout() 호출을 자연 스킵한다 (#1567).
const handleLoginChainBlocked = (code) => {
  const authType = 'ip-blocked'
  setTempEnvParam('authType', authType)
  handleAccessBlocked(code, { skipLogout: true })
  removeLocalAppUserKeys()
  return { authType }
}

const errorHandler = (title, content, func = () => {}, triggerCode = null) => {
  errorBase({
    title,
    content,
    onOk: () => {
      captureForcedLogout('landing.account.error-handler', {
        triggerCode,
      })
      reduxStore.dispatch({
        type: 'user/LOGOUT',
      })
      func()
    },
    okText: i18n.t('common.button.ok', {
      defaultValue: 'OK',
    }),
  })
}

export async function currentAccount() {
  const workspaceId = store.get('app.user.workspace_id') || getTempEnvParam('workspaceId')
  const token = store.get('app.user.token')
  const email = store.get('app.user.email') || store.get('envParem.userEmail')
  setEnvParam('userEmail', email)

  // 토큰이 없는 경우 로그아웃 처리.
  if (workspaceId == null || token == null) {
    captureForcedLogout('landing.account.current-account.token-null', {
      hasToken: !!token,
      hasWorkspaceId: !!workspaceId,
      hasRefreshToken: !!store.get('app.user.refreshToken'),
    })
    reduxStore.dispatch({
      type: 'user/LOGOUT',
    })
    return null
  }

  let authType = null
  let provider = null
  let displayName = null
  let myInfo = null
  let avatarUrl = null
  let userId = null
  let workspaceInfo = null
  let workspace = null
  // 가입 전(register)에는 /me 접근 불가 → auth/info 의 wrkp_initialized 로 Server 옵션 게이팅
  let authInfoWrkpInitialized = false
  // Fix C: auth/info 의 wrkp_public_key — 신규 invitee 가 ESCROW escrow 봉인에 사용(redux 경유).
  let authInfoWrkpPublicKey = null

  try {
    // auth info
    const umkRes = await getAuthInfo()
    const { code, data } = umkRes.data
    console.log('auth info - ', code, data)

    if (code === 200) {
      authType = data.auth_type
      // SAML 사용자 식별용 — JWT provider claim. auth_type은 가입 상태(login/register/...)만
      // 반환하므로 SAML 여부를 정확히 판단하려면 별도 필드 필요.
      provider = data.provider
      authInfoWrkpInitialized = data.wrkp_initialized === true
      authInfoWrkpPublicKey = data.wrkp_public_key ?? null
      console.log(' - authType : ', authType, ' provider : ', provider)
      setTempEnvParam('authType', authType)
      if (getTempEnvParam('ipBlocked')?.blocked === true) {
        removeTempEnvParam('ipBlocked')
      }

      if (data.user_key) {
        setTempEnvParam('base64privateKey', data.user_key)
      }
      // change-password 세션(임시비번)은 login/me 미호출 — auth/info 응답에서 복구 공개키 동기화 (#1320).
      // 'recovery_pub_key' in data 검사 내장 → 다른 auth_type(필드 없음)에선 no-op로 기존 캐시 유지.
      syncRecoveryPubKeyFromAuthData(data)
    } else if (code === 507 || code === 508) {
      errorHandler(
        i18n.t('admin.activity.failedLogin'),
        i18n.t('login.error.orgChartSyncError'),
        () => {},
        code,
      )
    } else if (code === 870) {
      authType = 'ip-blocked'
      setTempEnvParam('authType', authType)
      setTempEnvParam('ipBlocked', { blocked: true, timestamp: Date.now() })
      // history.push('/ip-blocked')
      return { authType }
    }
  } catch (e) {
    console.log(' - getUMK error : ', e)
    if (e.message === '509') {
      errorHandler(
        i18n.t('admin.activity.failedLogin'),
        i18n.t('workspace.error.509'),
        () => {
          history.push(DEFAULT_AUTH_PAGE)
        },
        509,
      )
    }
  }

  const isLogin =
    authType !== 'register' &&
    authType !== 'change-password' &&
    authType !== null &&
    authType !== 'ip-blocked'
  // users/me
  if (isLogin) {
    const userInfo = await getMyInfo()
    const { data } = userInfo
    myInfo = data
  }

  // orgChartData
  try {
    const orgChartData = await helper.startLoading()
    console.log('orgChartData: ', orgChartData)

    displayName = getUserDisplayName(email, orgChartData)
    avatarUrl = getUserAvatar(email, orgChartData)
    userId = getUserID(email, orgChartData)

    // orgChartData를 사용해 displayName 생성
    if (!displayName) {
      const [emailUser] = myInfo.user_email.split('@')
      displayName = emailUser
    }
  } catch (error) {
    console.error('Error in stopLoading:', error)
  }

  // workspace/info
  if (workspaceId != null) {
    try {
      const response = await getWorkspaceInfo()
      const { data } = response
      workspaceInfo = data
      console.log(' response - ', data)
    } catch (e) {
      console.log(' - getWorkspaceInfo error : ', e)
      notification.error({
        message: e.code,
        description: e.message,
      })
      return null
    }

    workspace = {
      id: workspaceId,
      workspace_name: workspaceInfo.workspace_name,
      role: workspaceInfo.role,
    }
  }

  // settings
  await getSettings()

  const result = {
    id: isLogin ? myInfo.id : userId,
    email: isLogin ? myInfo.user_email : email,
    name: displayName || '',
    avatar: avatarUrl,
    role: myInfo?.role,
    workspace,
    authType, // : 'change-password',
    provider,
    // PR2 H1: 복구정책(per-ws) — Settings 드롭다운 현재값/게이팅용. /me 응답(get_comprehensive_info)에 포함.
    recovery_option: isLogin ? myInfo?.recovery_option : null,
    // 워크스페이스 복구키(WRKP) 초기화 여부 — Settings/Register Server 옵션 게이팅용.
    // 로그인 후엔 /me 응답, 가입 전(register)엔 auth/info 응답에서 read.
    wrkp_initialized: isLogin ? myInfo?.wrkp_initialized === true : authInfoWrkpInitialized,
    // Fix C: 로그인 분기는 null(Settings는 /me/wrkp-public-keys 유지) — register 분기만 auth/info 값 전달.
    wrkp_public_key: isLogin ? null : authInfoWrkpPublicKey,
  }

  console.log(' --- current info : ', result)

  return result
}

export async function currentBasicAccount() {
  const inviteType = JSON.parse(getEnvParam('inviteType') || '{}')
  console.log(' - current inviteType : ', inviteType)
  const workspaceId = store.get('app.user.workspace_id')
  const email = store.get('app.user.email') || inviteType.invitee_email || getEnvParam('userEmail')
  const invitedToken = store.get('app.user.invite_token')
  const token = store.get('app.user.token')

  // invite 정보가 있고 토큰이 없는 경우 invite 정보 반환
  if (invitedToken != null && Object.keys(inviteType).length > 0 && token == null) {
    const inviteResult = {
      id: null,
      email: inviteType.invitee_email,
      name: null,
      avatar: null,
      role: inviteType?.role,
      workspace: {
        id: inviteType?.workspace_id,
        workspace_name: inviteType?.workspace_name,
        role: inviteType?.role,
      },
      authType: inviteType?.flow_type,
      workspaceList: null,
    }
    console.log(' --- current info - invite : ', inviteResult)
    return inviteResult
  }

  // 토큰이 없는 경우 로그아웃 처리.
  if (localStorage.getItem('app.user.token') == null) {
    captureForcedLogout('landing.account.current-basic-account.token-null', {
      hasToken: false,
      hasRefreshToken: !!store.get('app.user.refreshToken'),
    })
    reduxStore.dispatch({
      type: 'user/LOGOUT',
    })
    return null
  }

  let authType = getEnvParam('expiredData') ? 'expired' : 'register'
  let provider = null
  let workspaceList = null
  let myInfo = null

  let workspaceInfo = null
  let workspace = null
  let paidLicense = null
  // 가입 전(register)에는 /me 접근 불가 → auth/info 의 wrkp_initialized 로 Server 옵션 게이팅
  let authInfoWrkpInitialized = false
  // Fix C: auth/info 의 wrkp_public_key — 신규 invitee 가 ESCROW escrow 봉인에 사용(redux 경유).
  let authInfoWrkpPublicKey = null

  if (token != null) {
    try {
      // 설계 §3.3 — 메모리 이관. §9-Q2(이 분기가 실제 운영에서 도달하는가)는
      // 미해결이므로 **제거하지 않고** 저장소만 옮긴다. 남겨두면 도달 시 동작이
      // 유지되고, 미도달이면 별도 판단으로 삭제한다.
      if (getTempEnvParam('common_auth_changePW')) {
        authType = 'common_auth_changePW'
        setTempEnvParam('authType', authType)
      }

      if (workspaceId == null && inviteType?.workspace_id == null) {
        const commonAuthWorkspace = await getCommonAuthWorkspaceInfo()
        const { code: commonAuthWorkspaceCode, data: commonAuthWorkspaceData } =
          commonAuthWorkspace.data
        console.log(' - commonAuthWorkspace : ', commonAuthWorkspaceCode, commonAuthWorkspaceData)

        if (commonAuthWorkspaceData.workspace_id != null) {
          store.set('app.user.workspace_id', commonAuthWorkspaceData.workspace_id)
        }
      }

      const workspaceRes = await getWorkspacesByEmail()
      const { code: workspaceListCode, data: workspaceListData } = workspaceRes.data
      workspaceList = workspaceListData
      console.log(' - workspaceList : ', workspaceListCode, workspaceListData)

      if (workspaceRes.origin.code === 780) {
        authType = 'expired'
        setEnvParam('expiredData', workspaceRes.origin.data)
        history.push('/expired')
      } else if ([870, 879].includes(workspaceListCode)) {
        return handleLoginChainBlocked(workspaceListCode)
      }

      if (store.get('app.user.workspace_id') == null) {
        store.set('app.user.workspace_id', inviteType?.workspace_id || workspaceListData[0]?.id)
      }

      const workspaceIdList = workspaceListData.map((w) => w.id)
      const isWorkspaceIdInList = workspaceIdList.includes(
        Number(store.get('app.user.workspace_id')),
      )

      if (!isWorkspaceIdInList && workspaceListData?.length > 0) {
        store.set('app.user.workspace_id', workspaceListData[0]?.id)
      }

      const finalWorkspaceId = store.get('app.user.workspace_id')

      const [umkRes, userInfo, paidLicenseData, workspaceInfoResponse] = await Promise.all([
        getAuthInfo(),
        getMyInfo(),
        getPaidLicense(),
        finalWorkspaceId != null ? getWorkspaceInfo() : Promise.resolve(null),
        getSettings(),
      ])

      const { code, data } = umkRes.data
      console.log('auth info - ', code, data)
      if (code === 200) {
        authType = data.auth_type
        // SAML 사용자 식별용 — JWT provider claim (auth_type은 가입 상태만 반환).
        provider = data.provider
        authInfoWrkpInitialized = data.wrkp_initialized === true
        authInfoWrkpPublicKey = data.wrkp_public_key ?? null
        console.log(' - authType : ', authType, ' provider : ', provider)
        removeEnvParam('expiredData')
        setTempEnvParam('authType', authType)
        if (getTempEnvParam('ipBlocked')?.blocked === true) {
          removeTempEnvParam('ipBlocked')
        }

        if (data.user_key != null) {
          setTempEnvParam('base64privateKey', data.user_key)
        }
        // currentAccount와 동일 — change-password 세션의 복구 공개키 캐시 동기화 (#1320).
        syncRecoveryPubKeyFromAuthData(data)
      } else if (umkRes.data.code === 780) {
        authType = 'expired'
        setEnvParam('expiredData', umkRes.origin.data || umkRes.data.data)
        history.push('/expired')
      } else if ([870, 879].includes(code)) {
        // by-email 통과 직후 차단이 활성화되는 레이스 커버 — currentAccount의 auth/info 870 처리와 대칭
        return handleLoginChainBlocked(code)
      }

      store.set('app.user.email', userInfo?.data?.user_email || email)
      myInfo = userInfo?.data

      if (workspaceInfoResponse != null) {
        workspaceInfo = workspaceInfoResponse.data
        console.log(' response - ', workspaceInfoResponse)
      }

      const avatar = await getMyAvatar()
      console.log(' - avatar : ', avatar)
      myInfo.avatar = avatar?.data?.avatar?.url

      const selectedWorkspace = workspaceListData?.find(
        (w) => w.id.toString() === workspaceId?.toString(),
      )

      workspace = {
        id: workspaceId || workspaceInfo?.workspace_id,
        workspace_name: selectedWorkspace?.name || workspaceInfo?.workspace_name,
        role: selectedWorkspace?.role || workspaceInfo?.role,
      }

      if (workspaceInfo?.workspace_id != null) {
        await selectWorkspace(workspaceInfo.workspace_id)
        // const { code: code3, data: data3 } = res2.data
        // console.log(' - getWorkspaceInfo by email : ', code3, data3)
      }

      paidLicense = paidLicenseData?.data
    } catch (e) {
      console.log(' - getWorkspaceList error : ', e)
    }
  }

  // register가 된 경우
  const isLogin = authType !== 'register' && authType !== 'change-password' && authType !== null
  const result = {
    id: isLogin ? myInfo?.id : store.get('app.user.id') || null,
    email: isLogin ? myInfo?.user_email : email,
    name: myInfo?.user_name || store.get('app.user.name') || '',
    avatar: myInfo?.avatar || null,
    role: myInfo?.role,
    workspace: workspace != null ? workspace : null,
    authType,
    provider,
    workspaceList,
    paidLicense,
    // PR2 H1: 복구정책(per-ws) — currentAccount 와 동일하게 isGlobal 경로도 read.
    recovery_option: isLogin ? myInfo?.recovery_option : null,
    // 워크스페이스 복구키(WRKP) 초기화 여부 — currentAccount 와 동일하게 isGlobal 경로도 read.
    // 가입 전(register)엔 auth/info 응답에서 read.
    wrkp_initialized: isLogin ? myInfo?.wrkp_initialized === true : authInfoWrkpInitialized,
    // Fix C: 로그인 분기는 null(Settings는 /me/wrkp-public-keys 유지) — register 분기만 auth/info 값 전달.
    wrkp_public_key: isLogin ? null : authInfoWrkpPublicKey,
  }

  const inviteResult = {
    id: null,
    email,
    name: null,
    avatar: null,
    role: inviteType?.role,
    workspace: {
      id: inviteType?.workspace_id,
      workspace_name: inviteType?.workspace_name,
      role: inviteType?.role,
    },
    authType: inviteType?.flow_type,
    workspaceList: null,
  }

  const safebox = {
    userId: result.id,
    role: myInfo?.role,
    email: result.email,
    name: result.name,
  }
  setEnvParam('appType', JSON.stringify(safebox))

  console.log(' --- current info : ', result)

  return token != null ? result : inviteResult
}
